import React, { useState, FormEvent, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Cookies from "js-cookie";
import api from "../../config/configApi";
import axios from "axios";
import { toast, ToastContainer, ToastOptions } from "react-toastify";
import {
  FaRegEdit,
  FaStethoscope,
  FaPills,
  FaSyringe,
  FaUserMd,
  FaAmbulance,
  FaFirstAid,
  FaClinicMedical,
  FaHospital,
  FaMicroscope,
  FaMedkit,
  FaChartBar,
  FaTemperatureHigh,
  FaUser,
  FaEye,
  FaEyeSlash,
  FaSignInAlt,
  FaShieldAlt,
  FaCalendar,
  FaRibbon,
} from "react-icons/fa";

// Importar as configurações de tema
import {
  getCurrentTheme,
  getRandomMotivationalMessage,
  getCampaignText,
  ThemeConfig,
} from "../../config/themeConfig";

interface CustomJwtPayload {
  sub: string;
  roles: Array<{
    authority: string;
  }>;
  iat?: number;
  exp?: number;
}

// Interface para a resposta do login
interface LoginResponse {
  token: string;
}

interface LoginApiResponse {
  success: boolean;
  message: string;
  token?: string;
  role?: string;
  username?: string;
}

interface UsuarioResponse {
  username: string;
  name: string;
  phone: string;
  email: string;
  role: string;
  sad: boolean;
  active: string;
}

// Interface para o profissional (baseada na resposta da API)
interface ProfissionalData {
  idProfissional: number;
  nmProfissional?: string;
  login?: string;
  email?: string;
  cdConselho?: string;
  especialidade?: {
    idEspecialidade?: number;
    dsEspecialidade?: string;
    guiaSadt?: boolean;
    receituarioComum?: boolean;
    receituarioEspecial?: boolean;
    atestado?: boolean;
    laudo?: boolean;
    oftalmo?: boolean;
    altaFisio?: boolean;
  };
  procedimentos?: Array<{
    cdProcedimento: string;
    nmProcedimento: string;
  }>;
  status?: string;
  cdMunicipioAtend?: number;
  agendaOnline?: boolean;
  [key: string]: any;
}

const loginApi = async (
  username: string,
  password: string
): Promise<LoginApiResponse> => {
  try {
    // Fazer login para obter o token
    const loginResponse = await api.post("/sisclinic/auth/login", {
      username: username,
      password: password,
    });

    const loginData = loginResponse.data as LoginResponse;

    if (!loginData.token) {
      return {
        success: false,
        message: "Token não recebido do servidor",
      };
    }

    const token = loginData.token;

    // Decodificar o token para obter as informações
    const decodedToken = jwtDecode<CustomJwtPayload>(token);

    // Extrair a role principal do token
    let userRole = "";
    let userId = decodedToken.sub; // O username está no campo 'sub'

    if (decodedToken.roles && decodedToken.roles.length > 0) {
      // Remove o prefixo "ROLE_" se existir
      const roleAuthority = decodedToken.roles[0].authority;
      userRole = roleAuthority.replace("ROLE_", "");
    }

    return {
      success: true,
      message: "Login realizado com sucesso!",
      token: token,
      role: userRole,
      username: userId,
    };
  } catch (error) {
    console.error("Erro no login:", error);

    let errorMessage = "Erro ao fazer login";

    // Verificar se é um erro do Axios
    if (error && typeof error === "object" && "response" in error) {
      const axiosError = error as any;

      if (axiosError.response) {
        // Erro da API
        if (axiosError.response.status === 401) {
          errorMessage = "Usuário ou senha inválidos";
        } else if (axiosError.response.status === 400) {
          errorMessage = "Dados de login inválidos";
        } else if (
          axiosError.response.data &&
          axiosError.response.data.message
        ) {
          errorMessage = axiosError.response.data.message;
        } else {
          errorMessage = `Erro ${axiosError.response.status}: ${axiosError.response.statusText}`;
        }
      } else if (axiosError.request) {
        // Erro de rede
        errorMessage = "Erro de conexão com o servidor";
      } else {
        // Outros erros
        errorMessage = axiosError.message || "Erro desconhecido";
      }
    } else if (error instanceof Error) {
      // Erro genérico
      errorMessage = error.message;
    }

    return {
      success: false,
      message: errorMessage,
    };
  }
};

// Função para embaralhar o array
const shuffleArray = (array: any[]) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
};

// =============================================
// COMPONENTE ORIGINAL (para meses normais)
// =============================================

const OriginalFloatingIcons = () => {
  const positions = [
    { top: "10%", left: "5%" },
    { top: "38%", left: "12%" },
    { top: "80%", left: "8%" },
    { top: "70%", left: "20%" },
    { top: "10%", right: "5%" },
    { top: "30%", right: "10%" },
    { top: "50%", right: "8%" },
    { top: "70%", right: "12%" },
    { top: "5%", left: "25%" },
    { top: "-6%", left: "50%" },
    { top: "2%", left: "73%" },
    { bottom: "2%", left: "30%" },
    { bottom: "-5%", left: "50%" },
    { bottom: "2%", left: "70%" },
    { top: "60%", left: "60%" },
    { top: "50%", left: "40%" },
  ];

  const icons = [
    {
      icon: <FaStethoscope size={30} className="text-blue-500" />,
      title: "Consultas",
    },
    { icon: <FaUserMd size={30} className="text-red-500" />, title: "Médicos" },
    {
      icon: <FaChartBar size={30} className="text-green-500" />,
      title: "Prontuários",
    },
    {
      icon: <FaRegEdit size={30} className="text-purple-500" />,
      title: "Atendimentos",
    },
    {
      icon: <FaPills size={30} className="text-yellow-500" />,
      title: "Receituários",
    },
    {
      icon: <FaClinicMedical size={30} className="text-indigo-500" />,
      title: "Clínica",
    },
    {
      icon: <FaHospital size={30} className="text-pink-500" />,
      title: "Hospital",
    },
    { icon: <FaMicroscope size={30} className="text-teal-500" />, title: "Exames" },
    {
      icon: <FaMedkit size={30} className="text-orange-500" />,
      title: "Procedimentos",
    },
    {
      icon: <FaTemperatureHigh size={30} className="text-red-600" />,
      title: "Ultrassom",
    },
    {
      icon: <FaFirstAid size={30} className="text-blue-600" />,
      title: "Atestados",
    },
    {
      icon: <FaSyringe size={30} className="text-green-600" />,
      title: "Injeções",
    },
    {
      icon: <FaPills size={30} className="text-purple-600" />,
      title: "Medicamentos",
    },
    {
      icon: <FaMicroscope size={30} className="text-yellow-600" />,
      title: "Laboratório",
    },
    {
      icon: <FaAmbulance size={30} className="text-indigo-600" />,
      title: "Emergência",
    },
    {
      icon: <FaUserMd size={30} className="text-gray-600" />,
      title: "Administrativo",
    },
    {
      icon: <FaChartBar size={30} className="text-blue-700" />,
      title: "Relatórios",
    },
    {
      icon: <FaRegEdit size={30} className="text-green-700" />,
      title: "Dados",
    },
    {
      icon: <FaShieldAlt size={30} className="text-gray-700" />,
      title: "Configurações",
    },
    {
      icon: <FaUserMd size={30} className="text-purple-700" />,
      title: "Usuários",
    },
    {
      icon: <FaCalendar size={30} className="text-red-700" />,
      title: "Agenda",
    },
    {
      icon: <FaRegEdit size={30} className="text-orange-700" />,
      title: "Registros",
    },
    {
      icon: <FaShieldAlt size={30} className="text-yellow-700" />,
      title: "Notificações",
    },
    {
      icon: <FaRegEdit size={30} className="text-indigo-700" />,
      title: "Mensagens",
    },
  ];

  shuffleArray(positions);
  shuffleArray(icons);

  return (
    <>
      {positions.map((position, index) => (
        <motion.div
          key={index}
          className="absolute group"
          style={{
            ...position,
            zIndex: 10,
          }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 1,
            delay: index * 0.1,
            repeat: Infinity,
            repeatType: "reverse",
            repeatDelay: 3,
          }}
          title={icons[index % icons.length].title}
        >
          {icons[index % icons.length].icon}
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            {icons[index % icons.length].title}
          </div>
        </motion.div>
      ))}
    </>
  );
};

// =============================================
// COMPONENTE TEMÁTICO (para meses temáticos)
// =============================================

const ThemedFloatingIcons = ({ theme }: { theme: ThemeConfig }) => {
  const positions = [
    { top: "10%", left: "5%" },
    { top: "38%", left: "12%" },
    { top: "80%", left: "8%" },
    { top: "70%", left: "20%" },
    { top: "10%", right: "5%" },
    { top: "30%", right: "10%" },
    { top: "50%", right: "8%" },
    { top: "70%", right: "12%" },
    { top: "5%", left: "25%" },
    { top: "-6%", left: "50%" },
    { top: "2%", left: "73%" },
    { bottom: "2%", left: "30%" },
    { bottom: "-5%", left: "50%" },
    { bottom: "2%", left: "70%" },
    { top: "60%", left: "60%" },
    { top: "50%", left: "40%" },
  ];

  // Usa as cores do tema dinâmico
  const getIconColor = (index: number) => {
    const colorClasses = [
      theme.colors.accent,
      theme.colors.primary,
      theme.colors.secondary,
      theme.colors.text,
    ];
    return colorClasses[index % colorClasses.length];
  };

  const icons = [
    { icon: FaStethoscope, title: "Consultas" },
    { icon: FaUserMd, title: "Médicos" },
    { icon: FaChartBar, title: "Prontuários" },
    { icon: FaRegEdit, title: "Atendimentos" },
    { icon: FaPills, title: "Receituários" },
    { icon: FaClinicMedical, title: "Clínica" },
    { icon: FaHospital, title: "Hospital" },
    { icon: FaMicroscope, title: "Exames" },
    { icon: FaMedkit, title: "Procedimentos" },
    { icon: FaTemperatureHigh, title: "Ultrassom" },
    { icon: FaFirstAid, title: "Atestados" },
    { icon: FaSyringe, title: "Injeções" },
    { icon: FaPills, title: "Medicamentos" },
    { icon: FaMicroscope, title: "Laboratório" },
    { icon: FaAmbulance, title: "Emergência" },
    { icon: FaUserMd, title: "Administrativo" },
    { icon: FaChartBar, title: "Relatórios" },
    { icon: FaRegEdit, title: "Dados" },
    { icon: FaShieldAlt, title: "Configurações" },
    { icon: FaUserMd, title: "Usuários" },
    { icon: FaCalendar, title: "Agenda" },
    { icon: FaRegEdit, title: "Registros" },
    { icon: FaShieldAlt, title: "Notificações" },
    { icon: FaRegEdit, title: "Mensagens" },
    { icon: FaRibbon, title: theme.messages.title },
  ];

  shuffleArray(positions);
  shuffleArray(icons);

  return (
    <>
      {positions.map((position, index) => {
        const IconComponent = icons[index % icons.length].icon;
        return (
          <motion.div
            key={index}
            className="absolute group"
            style={{
              ...position,
              zIndex: 10,
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{
              opacity: 1,
              scale: 1,
              y: [0, -10, 0],
            }}
            transition={{
              duration: 3,
              delay: index * 0.2,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            title={icons[index % icons.length].title}
          >
            <IconComponent size={30} className={getIconColor(index)} />
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
              {icons[index % icons.length].title}
            </div>
          </motion.div>
        );
      })}
    </>
  );
};

// =============================================
// COMPONENTE ANO NOVO (26 a 31 de dezembro)
// =============================================

const NewYearFloatingIcons = ({ theme }: { theme: ThemeConfig }) => {
  const positions = [
    { top: "10%", left: "5%" },
    { top: "38%", left: "12%" },
    { top: "80%", left: "8%" },
    { top: "70%", left: "20%" },
    { top: "10%", right: "5%" },
    { top: "30%", right: "10%" },
    { top: "50%", right: "8%" },
    { top: "70%", right: "12%" },
    { top: "5%", left: "25%" },
    { top: "-6%", left: "50%" },
    { top: "2%", left: "73%" },
    { bottom: "2%", left: "30%" },
    { bottom: "-5%", left: "50%" },
    { bottom: "2%", left: "70%" },
    { top: "60%", left: "60%" },
    { top: "50%", left: "40%" },
  ];

  const newYearIcons = [
    { icon: "🎆", title: "Fogos de Artifício" },
    { icon: "🎇", title: "Celebração" },
    { icon: "✨", title: "Brilho" },
    { icon: "🎊", title: "Confetes" },
    { icon: "🥳", title: "Festa" },
    { icon: "🎉", title: "Celebração" },
    { icon: "🕛", title: "Meia-noite" },
    { icon: "⭐", title: "Estrelas" },
    { icon: "🌟", title: "Brilho" },
    { icon: "💫", title: "Faíscas" },
    { icon: "🎈", title: "Balões" },
    { icon: "🎁", title: "Presentes" },
    { icon: "🥂", title: "Brinde" },
    { icon: "🍾", title: "Champagne" },
    { icon: "🎄", title: "Natal" },
    { icon: "🔔", title: "Sinos" },
    { icon: "🕊️", title: "Paz" },
    { icon: "❤️", title: "Amor" },
    { icon: "🙏", title: "Bênçãos" },
    { icon: "🎅", title: "Papai Noel" },
  ];

  shuffleArray(positions);
  shuffleArray(newYearIcons);

  return (
    <>
      {positions.map((position, index) => {
        const icon = newYearIcons[index % newYearIcons.length];
        return (
          <motion.div
            key={index}
            className="absolute group"
            style={{
              ...position,
              zIndex: 10,
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{
              opacity: 1,
              scale: 1,
              y: [0, -20, 0],
              rotate: [0, 10, -10, 0],
            }}
            transition={{
              duration: 4,
              delay: index * 0.1,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            title={icon.title}
          >
            <div className="text-4xl animate-pulse">{icon.icon}</div>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
              {icon.title}
            </div>
          </motion.div>
        );
      })}
    </>
  );
};

// Componente de banner temático
interface ThemeBannerProps {
  theme: ThemeConfig;
}

const ThemeBanner = ({ theme }: ThemeBannerProps) => {
  const campaign = getCampaignText(theme);
  const [currentMessage, setCurrentMessage] = useState("");

  useEffect(() => {
    setCurrentMessage(getRandomMotivationalMessage(theme));
    const interval = setInterval(() => {
      setCurrentMessage(getRandomMotivationalMessage(theme));
    }, 8000);

    return () => clearInterval(interval);
  }, [theme]);

  const getThemeIcon = () => {
    switch (theme.theme) {
      case "yellow":
        return "💛";
      case "pink":
        return "💖";
      case "blue":
        return "💙";
      case "red":
        return "🎄";
      case "gold":
        return "🎆";
      default:
        return "🏥";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`mb-6 p-4 rounded-xl bg-gradient-to-r ${theme.gradients.main} text-white shadow-lg`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="text-2xl animate-pulse">{getThemeIcon()}</div>
          <div>
            <h3 className="font-bold text-lg">{campaign.title}</h3>
            <p className="text-sm opacity-90">{campaign.subtitle}</p>
          </div>
        </div>
        <motion.div
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-right"
        >
          <p className="text-sm font-medium">{currentMessage}</p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [message, setMessage] = useState<string>("");
  const navigate = useNavigate();

  // Obter tema atual baseado no mês
  const currentTheme = getCurrentTheme();
  const isThemedMonth = currentTheme.theme !== "default";
  
  // Verifica se é período de Ano Novo (26 a 31 de dezembro)
  const now = new Date();
  const isNewYearPeriod =
    now.getMonth() === 11 && now.getDate() >= 26 && now.getDate() <= 31;

  // Configurar toast com cores do tema
  const toastConfig: ToastOptions = {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    style: {
      backgroundColor: isThemedMonth
        ? (() => {
            switch (currentTheme.theme) {
              case "yellow":
                return "#ca8a04";
              case "pink":
                return "#db2777";
              case "blue":
                return "#1d4ed8";
              case "red":
                return "#dc2626";
              case "gold":
                return "#f59e0b";
              default:
                return "black";
            }
          })()
        : "black",
      color: "white",
    },
  };

  // Configurações para o layout temático - DINÂMICAS
  const getThemedConfig = () => {
    return {
      gradient: currentTheme.gradients.main,
      gradientLight: currentTheme.gradients.light,
      brandGradient: currentTheme.gradients.brand,
      buttonGradient: currentTheme.gradients.button,
      buttonHover: currentTheme.gradients.buttonHover,
      accentColor: currentTheme.colors.accent,
      ribbonIcon: <FaRibbon className={currentTheme.colors.accent} />,
    };
  };

  const themedConfig = getThemedConfig();

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setButtonDisabled(true);
    setMessage("Autenticando...");

    try {
      // ===============================
      // 🔐 LOGIN
      // ===============================
      const response = await loginApi(username, password);

      if (!response.success || !response.token || !response.role) {
        setMessage(response.message);
        toast.error(response.message || "Erro ao fazer login!", toastConfig);
        // ⬇️ REABILITAÇÃO IMEDIATA
        setButtonDisabled(false);
        setLoading(false);
        return;
      }

      const token = response.token;
      localStorage.setItem("Frontend", token);

      const decodedToken = jwtDecode<CustomJwtPayload>(token);
      let userRole = response.role;
      const userId = decodedToken.sub;

      if (decodedToken.roles?.length) {
        userRole = decodedToken.roles[0].authority.replace("ROLE_", "");
      }

      Cookies.set("loginTime", Date.now().toString(), { expires: 1 });
      Cookies.set("UserRole", userRole);
      Cookies.set("Username", userId);
      Cookies.set("UserId", userId);

      // ===============================
      // 🔍 BUSCAR USUÁRIO / VALIDAR SAD
      // ===============================
      const usuarioResponse = await api.get<UsuarioResponse>(
        `/sisclinic/usuarios/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const usuario = usuarioResponse.data;
      Cookies.set("sad", usuario.sad.toString());

      // 🚫 BLOQUEIO SAD
      if (userRole === "PROFISSIONAL" && usuario.sad !== true) {
        toast.error(
          "Seu usuário não possui permissão para acessar o módulo SAD.",
          toastConfig
        );

        localStorage.removeItem("Frontend");
        
        // ⬇️ LIMPA A MENSAGEM E REABILITA
        setMessage("");
        setButtonDisabled(false);
        setLoading(false);

        setTimeout(() => {
          navigate("/");
        }, 2000);

        return;
      }

      // ===============================
      // 👨‍⚕️ BUSCAR PROFISSIONAL
      // ===============================
      const axiosInstance = axios.create({
        baseURL: api.defaults.baseURL,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const profissionalResponse = await axiosInstance.get(
        `/sisclinic/profissionais/login/${username}`
      );

      if (
        !profissionalResponse.data ||
        !profissionalResponse.data.idProfissional
      ) {
        console.warn(
          "Resposta da API do profissional inválida:",
          profissionalResponse.data
        );

        Cookies.set("idProfissional", "0");
        Cookies.set("nmProfissional", username);
        Cookies.set("idEspecialidade", "0");
        Cookies.set("dsEspecialidade", "Não especificada");

        toast.warning(
          "Login realizado, mas não foi possível obter informações completas do profissional.",
          toastConfig
        );

        // ⬇️ LIMPA A MENSAGEM E REABILITA
        setMessage("");
        setButtonDisabled(false);
        setLoading(false);

        setTimeout(() => {
          navigate("/home");
          window.location.reload();
        }, 3000);

        return;
      }

      const profissional = profissionalResponse.data as ProfissionalData;

      const idProfissional = profissional.idProfissional;
      const nmProfissional = profissional.nmProfissional || username;

      const idsExcluidos = [
        2715968, 9959998, 2382177, 9960054, 6914686,
        9960053, 9960028, 223173, 7925015, 2148607, 9960008,
      ];

      const estaExcluido = idsExcluidos.includes(idProfissional);

      let idEspecialidade = "0";
      let dsEspecialidade = "Não especificada";

      if (profissional.especialidade) {
        idEspecialidade =
          profissional.especialidade.idEspecialidade?.toString() || "0";
        dsEspecialidade =
          profissional.especialidade.dsEspecialidade || "Não especificada";
      }

      Cookies.set("idProfissional", idProfissional.toString());
      Cookies.set("nmProfissional", nmProfissional);
      Cookies.set("idEspecialidade", idEspecialidade);
      Cookies.set("dsEspecialidade", dsEspecialidade);
      Cookies.set("estaExcluido", estaExcluido.toString());

      if (profissional.especialidade) {
        Cookies.set(
          "guiaSadt",
          profissional.especialidade.guiaSadt?.toString() || "false"
        );
        Cookies.set(
          "receituarioComum",
          profissional.especialidade.receituarioComum?.toString() || "false"
        );
        Cookies.set(
          "receituarioEspecial",
          profissional.especialidade.receituarioEspecial?.toString() || "false"
        );
        Cookies.set(
          "atestado",
          profissional.especialidade.atestado?.toString() || "false"
        );
        Cookies.set(
          "laudo",
          profissional.especialidade.laudo?.toString() || "false"
        );
      }

      // ===============================
      // ✅ SUCESSO
      // ===============================
      let count = 3;

      const toastId = toast.success(
        `Bem-vindo, ${nmProfissional}! Redirecionando em ${count} segundos...`,
        toastConfig
      );

      const countdown = setInterval(() => {
        count--;

        if (count > 0) {
          toast.update(toastId, {
            render: `${nmProfissional}, redirecionando em ${count} segundos...`,
            autoClose: 1000,
          });
        } else {
          clearInterval(countdown);
          navigate("/home");
          window.location.reload();
        }
      }, 1000);

    } catch (error) {
      console.error("Erro no login:", error);
      toast.error("Erro inesperado ao autenticar.", toastConfig);
      // ⬇️ LIMPA A MENSAGEM E REABILITA
      setMessage("");
      setButtonDisabled(false);
      setLoading(false);
    }
  };

  // Renderização condicional baseada no tema
  if (!isThemedMonth) {
    // =============================================
    // LAYOUT ORIGINAL (meses normais)
    // =============================================
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="absolute inset-0 overflow-hidden z-0">
          <OriginalFloatingIcons />
        </div>

        <div className="relative z-10 w-full max-w-6xl flex flex-col md:flex-row rounded-2xl overflow-hidden shadow-2xl">
          {/* Left Panel - Branding */}
          <div className="w-full md:w-2/5 bg-gradient-to-br from-blue-500 to-blue-700 text-white p-8 flex flex-col justify-between">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-center mb-8"
            >
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-white bg-opacity-90 rounded-2xl backdrop-blur-sm">
                  <img
                    src="https://i0.wp.com/afrafepsaude.com.br/wp-content/uploads/2021/06/logo.png?w=692&ssl=1"
                    alt="Logo AFRAFEP"
                    className="h-20 mx-auto"
                  />
                </div>
              </div>
              <h1 className="text-3xl font-bold mb-2">
                SAD - Sistema de Atendimento Domiciliar
                <br />
              </h1>
              <p className="text-blue-100">
                Acesso exclusivo para SAD
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="space-y-4 mt-8"
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <FaShieldAlt className="text-blue-200" />
                </div>
                <div>
                  <h3 className="font-semibold">Autenticação Segura</h3>
                  <p className="text-sm text-blue-200">
                    Protegemos seus dados com criptografia avançada
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <FaStethoscope className="text-blue-200" />
                </div>
                <div>
                  <h3 className="font-semibold">Acesso Para os Profissionais do SAD</h3>
                  <p className="text-sm text-blue-200">
                    Sistema exclusivo para os Profissionais de Saúde
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Panel - Login Form */}
          <div className="w-full md:w-3/5 bg-white p-10 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="max-w-md mx-auto w-full"
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-2">
                  Acesso dos
                  <br />
                  Profissionais de Saúde
                </h2>
                <p className="text-gray-800">
                  Entre com suas credenciais para acessar o sistema
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Usuário
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaUser className="h-5 w-5 text-gray-500" />
                    </div>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                      placeholder="Digite seu usuário"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Senha
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaShieldAlt className="h-5 w-5 text-gray-500" />
                    </div>
                    <input
                      type={passwordVisible ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                      placeholder="Digite sua senha"
                    />
                    <button
                      type="button"
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {passwordVisible ? (
                        <FaEye className="h-5 w-5 text-gray-500 hover:text-gray-700" />
                      ) : (
                        <FaEyeSlash className="h-5 w-5 text-gray-500 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>

                {message && !loading && (
                  <div
                    className={`p-3 rounded-lg text-center ${
                      message.includes("sucesso") ||
                      message.includes("Autenticando")
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {message}
                  </div>
                )}

                <div className="pt-4">
                  <motion.button
                    type="submit"
                    disabled={buttonDisabled || loading}
                    className={`w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 ${
                      buttonDisabled || loading
                        ? "opacity-70 cursor-not-allowed"
                        : ""
                    }`}
                    whileHover={
                      buttonDisabled || loading ? {} : { scale: 1.02 }
                    }
                    whileTap={buttonDisabled || loading ? {} : { scale: 0.98 }}
                  >
                    {loading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Autenticando...
                      </>
                    ) : (
                      <>
                        <FaSignInAlt className="mr-2" />
                        {buttonDisabled ? "Autenticando..." : "Entrar no Sistema"}
                      </>
                    )}
                  </motion.button>
                </div>
              </form>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="mt-8 text-center text-sm text-gray-500"
              >
                <p>
                  Problemas para acessar? Entre em contato com o suporte
                  técnico.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <ToastContainer />
      </div>
    );
  }

  // =============================================
  // LAYOUT TEMÁTICO (meses temáticos)
  // =============================================
  return (
    <div
      className={`min-h-screen bg-gradient-to-br ${themedConfig.gradientLight} flex items-center justify-center p-4 relative overflow-hidden`}
    >
      <div className="absolute inset-0 overflow-hidden z-0">
        {isNewYearPeriod ? (
          <NewYearFloatingIcons theme={currentTheme} />
        ) : (
          <ThemedFloatingIcons theme={currentTheme} />
        )}
      </div>

      <div className="relative z-10 w-full max-w-6xl flex flex-col md:flex-row rounded-2xl overflow-hidden shadow-2xl backdrop-blur-sm bg-white/90">
        {/* Left Panel - Branding */}
        <div
          className={`w-full md:w-2/5 bg-gradient-to-br ${themedConfig.gradient} text-white p-8 flex flex-col justify-between relative overflow-hidden`}
        >
          {/* Efeito de brilho */}
          <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center mb-8 relative z-10"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-white bg-opacity-90 rounded-2xl backdrop-blur-sm border border-white/20">
                <img
                  src="https://i0.wp.com/afrafepsaude.com.br/wp-content/uploads/2021/06/logo.png?w=692&ssl=1"
                  alt="Logo AFRAFEP"
                  className="h-20 mx-auto"
                />
              </div>
            </div>

            <div className="flex items-center justify-center mb-4">
              {isNewYearPeriod ? (
                <>
                  <div>
                    <h1 className="text-3xl font-bold">
                      SAD - Sistema de Atendimento Domiciliar
                    </h1>
                  </div>
                </>
              ) : (
                <>
                  {themedConfig.ribbonIcon && (
                    <motion.div
                   animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                      className="mr-3"
                    >
                      {themedConfig.ribbonIcon}
                    </motion.div>
                  )}                
                  <h1 className="text-3xl font-bold">
                    SAD - Sistema de Atendimento Domiciliar
                  </h1>
                </>
              )}
            </div>

            {/* Apenas uma mensagem de felicitações - CORRIGIDO */}
            <div className="mt-4 p-4 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
              <p className="text-white/90 font-semibold text-lg">
                {getCampaignText(currentTheme).title}
              </p>
              <p className="text-white/80 text-sm mt-2">
                {getCampaignText(currentTheme).subtitle}
              </p>
              {isNewYearPeriod && (
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  className="mt-2 flex items-center justify-center space-x-1"
                >
                  <span className="text-xs text-yellow-300 animate-pulse">
                    🎉 {getRandomMotivationalMessage(currentTheme)}
                  </span>
                </motion.div>
              )}
            </div>
          </motion.div>

          {isThemedMonth && !isNewYearPeriod && <ThemeBanner theme={currentTheme} />}

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="space-y-4 mt-8 relative z-10"
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm">
                <FaShieldAlt className="text-black" />
              </div>
              <div>
                <h3 className="font-semibold">Autenticação Segura</h3>
                <p className="text-sm text-white/80">
                  Protegemos seus dados com criptografia avançada
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm">
                <FaStethoscope className="text-black" />
              </div>
              <div>
                <h3 className="font-semibold">Acesso Para os Profissionais de Saúde</h3>
                <p className="text-sm text-white/80">
                  Sistema exclusivo para os Profissionais de Saúde
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="w-full md:w-3/5 bg-white/95 p-10 flex flex-col justify-center backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="max-w-md mx-auto w-full"
          >
            <div className="text-center mb-8">
              <h2
                className={`text-3xl font-bold ${themedConfig.accentColor} mb-2`}
              >
                Acesso dos
                <br />
                Profissionais do SAD
              </h2>
              <p className="text-gray-800">
                Entre com suas credenciais para acessar o sistema
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Usuário
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className={`h-5 w-5 ${themedConfig.accentColor}`} />
                  </div>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                    placeholder="Digite seu usuário"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Senha
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaShieldAlt
                      className={`h-5 w-5 ${themedConfig.accentColor}`}
                    />
                  </div>
                  <input
                    type={passwordVisible ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                    placeholder="Digite sua senha"
                  />
                  <button
                    type="button"
                    onClick={() => setPasswordVisible(!passwordVisible)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {passwordVisible ? (
                      <FaEye
                        className={`h-5 w-5 ${themedConfig.accentColor} hover:opacity-70`}
                      />
                    ) : (
                      <FaEyeSlash
                        className={`h-5 w-5 ${themedConfig.accentColor} hover:opacity-70`}
                      />
                    )}
                  </button>
                </div>
              </div>

              {message && !loading && (
                <div
                  className={`p-3 rounded-lg text-center ${
                    message.includes("sucesso") ||
                    message.includes("Autenticando")
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {message}
                </div>
              )}

              <div className="pt-4">
                <motion.button
                  type="submit"
                  disabled={buttonDisabled || loading}
                 className={`w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r ${
  themedConfig.buttonGradient
} hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 ${
  buttonDisabled || loading
    ? "opacity-70 cursor-not-allowed"
    : ""
} active:brightness-90`}
                  whileHover={buttonDisabled || loading ? {} : { scale: 1.02 }}
                  whileTap={buttonDisabled || loading ? {} : { scale: 0.98 }}
                >
                  {loading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Autenticando...
                    </>
                  ) : (
                    <>
                      <FaSignInAlt className="mr-2" />
                      {buttonDisabled ? "Autenticando..." : "Entrar no Sistema"}
                    </>
                  )}
                </motion.button>
              </div>
            </form>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="mt-8 text-center text-sm text-gray-500"
            >
              <p>
                Problemas para acessar? Entre em contato com o suporte técnico.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
}